import { useCallback, useEffect, useRef, useState } from "react";
import type { GriddSnapshot } from "@gridd/embed/react";

import { Slide } from "@/components/deck/Slide";
import {
  evidenceBelow,
  pyramidStep,
  type PyramidDirection,
} from "@/lib/pyramid";
import { slideNumbering } from "@/lib/slide-numbering";
import { cn } from "@/lib/utils";
import { clampDuration, isAnimated, motionStyle } from "@/lib/slide-transition";
import type { SlideData, SlideTransition } from "@/types/document";

// Fullscreen single-slide presentation (TODO §5). Distinct from the editor's inline
// read-only preview: this is a `position: fixed` overlay that requests the Fullscreen API
// and drives one slide at a time by keyboard. The editor stays mounted *underneath* it, so
// exiting returns to the same scroll position (§5.7) with no re-hydration.
//
// It reuses the same `Slide` in `presenting` mode as the editor (§5.1), so what you present
// is pixel-identical to what you edited — only the scaling target differs (viewport here,
// column there).
//
// §18.3 Stepping is the PYRAMID's, not the array's: ←/→ move along the storyline and ↑/↓
// move between a storyline slide and its evidence, with the backup suffix flat. The rules all
// live in `pyramidStep`; this file only turns four keys into four directions and asks it
// where to go. A flat deck has one column per slide, so ←/→ is exactly the old ±1 and nothing
// about presenting such a deck changed — which is why there is no Pyramid/Linear mode toggle.
//
// §10 Slide transitions play here. On a step, the outgoing slide keeps mounting as a
// `.slide-leaving` layer while the incoming mounts as `.slide-entering`; CSS does the
// motion (styles.css + `motionStyle`), React only toggles classes and drops the leaver
// after the duration. A minimal state machine keeps at most two slides mounted, so rapid
// navigation short-circuits to the target rather than queueing (§10.4.3).

const noop = () => {};

type Leaving = { slide: SlideData; index: number; navDir: 1 | -1 };

export function PresentationView({
  slides,
  transition,
  snapshots,
  startIndex = 0,
  onExit,
}: {
  // The ACTIVE (non-archived) slides only — the caller filters, so indices here are the
  // presentation order and feed the on-slide page number directly (§5.3).
  slides: SlideData[];
  // Deck-level transition (§10) — stable for the duration of a presentation.
  transition: SlideTransition;
  // §5.5 Pre-captured grid snapshots keyed by griddDocId, taken off the live editor embeds
  // before this overlay mounts. Threaded to every Slide so its grid renders as a static image
  // rather than mounting a fresh iframe that would reload and break the immersion. Grids with
  // no snapshot (capture failed) fall back to a live embed. Stable for the presentation.
  snapshots: Record<string, GriddSnapshot>;
  startIndex?: number;
  onExit: () => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const clampedStart = Math.min(
    Math.max(startIndex, 0),
    Math.max(slides.length - 1, 0),
  );
  const [index, setIndex] = useState(clampedStart);
  const [navDir, setNavDir] = useState<1 | -1>(1);
  const [leaving, setLeaving] = useState<Leaving | null>(null);
  // Live index, so the keydown handler never reads a stale closure without re-binding.
  const idxRef = useRef(clampedStart);
  // One reset-able timer clears the leaver after the animation; resetting it on each step is
  // what makes rapid navigation collapse to the latest target (§10.4.3).
  const leaveTimer = useRef<number | null>(null);

  // §10.4.2 Respect reduced motion — treat it as an instant cut in JS (no second slide
  // mounted), belt-and-suspenders with the CSS media query.
  const [reducedMotion, setReducedMotion] = useState(
    () =>
      typeof window !== "undefined" &&
      !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const animated = isAnimated(transition.type) && !reducedMotion;

  // §18.3 One entry point for all four directions. The target comes from `pyramidStep`, which
  // returns `null` when the move runs off the end of its axis — the same "stay put" the old
  // index clamp produced at slide 0 and the last slide, so an arrow at an edge is still inert
  // rather than surprising.
  //
  // The transition's direction is derived from which way the INDEX moved, not from the key:
  // ↓ into evidence and → to the next storyline slide both travel forwards through the array,
  // and the deck has one configured transition (§10.1), not one per axis.
  const go = useCallback(
    (direction: PyramidDirection) => {
      const from = idxRef.current;
      const fromSlide = slides[from];
      if (!fromSlide) return;
      const targetId = pyramidStep(slides, fromSlide.id, direction);
      if (!targetId) return;
      const target = slides.findIndex((s) => s.id === targetId);
      if (target === -1 || target === from) return;
      const delta: 1 | -1 = target > from ? 1 : -1;
      idxRef.current = target;
      if (animated) {
        setNavDir(delta);
        setLeaving({ slide: slides[from], index: from, navDir: delta });
        if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
        leaveTimer.current = window.setTimeout(
          () => {
            setLeaving(null);
            leaveTimer.current = null;
          },
          clampDuration(transition.durationMs) + 80,
        );
      }
      setIndex(target);
    },
    [animated, slides, transition.durationMs],
  );

  useEffect(
    () => () => {
      if (leaveTimer.current) window.clearTimeout(leaveTimer.current);
    },
    [],
  );

  // The live `onExit`, read through a ref. The fullscreen effect below MUST run exactly
  // once per mount (see its comment), so it cannot take the callback as a dependency —
  // the editor passes a fresh arrow on every one of its renders.
  const onExitRef = useRef(onExit);
  useEffect(() => {
    onExitRef.current = onExit;
  });

  // §5.2 Fullscreen API: request it on the overlay on entry, release on exit. If the
  // browser drops fullscreen on its own — the user hit Esc at the browser-chrome level —
  // the `fullscreenchange` event fires with no `fullscreenElement`, which we treat as an
  // exit so React state never diverges from the actual fullscreen state.
  //
  // Mount-once (`[]`), deliberately. The editor stays live underneath this overlay and
  // re-renders while we present — its `ResizeObserver` alone fires the moment fullscreen
  // changes the viewport — and every one of those renders hands us a new `onExit`. Keyed
  // on it, this effect would tear down and re-run: the cleanup calls `exitFullscreen`, the
  // resulting `fullscreenchange` hits the re-attached listener with no `fullscreenElement`,
  // and the presentation closes the instant it opens. Chromium hides that in a browser by
  // coalescing the exit and the re-request, but Electron binds HTML fullscreen to the macOS
  // native window transition — a slow, uncoalescible animation — so there it always fires.
  useEffect(() => {
    const el = rootRef.current;
    // requestFullscreen can be refused (not a user gesture, or unsupported) — present in a
    // plain fullscreen-styled overlay instead rather than throwing.
    el?.requestFullscreen?.().catch(() => {});
    // Only a *drop* out of fullscreen is an exit. The macOS transition is slow enough to
    // dispatch a `fullscreenchange` before `fullscreenElement` is set, and a refused request
    // never sets it at all — neither should close an overlay that is still on its way in.
    let entered = false;
    const onFsChange = () => {
      if (document.fullscreenElement) entered = true;
      else if (entered) onExitRef.current();
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      if (document.fullscreenElement)
        document.exitFullscreen?.().catch(() => {});
    };
  }, []);

  // §5.3/§18.3.5 Keyboard nav, live only while presenting. Space is prevented so it doesn't
  // also scroll. Navigation routes through `go`, which drives the §10 transition.
  //
  // PageDown/PageUp and Space stay on the ←/→ axis deliberately: those are what a presenter
  // remote sends, and a remote has no third and fourth button. So a remote walks the argument
  // and the backup section — which is the whole deck as far as an audience is concerned — and
  // the evidence detour stays on the keys a hand is actually on.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowRight":
        case "PageDown":
        case " ":
          e.preventDefault();
          go("next");
          break;
        case "ArrowLeft":
        case "PageUp":
          e.preventDefault();
          go("prev");
          break;
        case "ArrowDown":
          e.preventDefault();
          go("down");
          break;
        case "ArrowUp":
          e.preventDefault();
          go("up");
          break;
        case "Escape":
          // The browser also exits fullscreen on Escape (→ fullscreenchange → onExit), but
          // call it directly too so a windowed (fullscreen-refused) present still exits.
          onExit();
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onExit]);

  const current = slides[index];
  if (!current) return null; // nothing active to present — caller also guards this

  // §19.0.3/§18.2.9 Numbered once for the whole deck rather than from the slide's position,
  // so a cover consumes no number, evidence reads as `4.2` and backups as `B7` — and
  // presentation agrees with the editor, the PDF and the exported HTML.
  const numbers = slideNumbering(slides);
  // §18.3.6, at its smallest. The down axis is invisible otherwise: a presenter who cannot see
  // that this slide has evidence under it will never press ↓, and the feature may as well not
  // exist. One chip, on the backdrop rather than on the canvas — nothing here may touch the
  // slide, which is the same 1600×900 canvas the PDF and the export render.
  const below = evidenceBelow(slides, current.id);

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black"
    >
      {/* §5.1 Letterbox: the 16:9 stage scales to whichever viewport dimension binds
          (width = min(vw, vh·16/9)), centered on the dark backdrop. `.slide-motion` layers
          fill the stage; `Slide` measures a layer and scales its 1600×900 canvas into it.

          §5.5 At most the current + (briefly) the leaving slide are mounted; each is keyed
          on its slide id, so a step remounts. Grids render from `snapshots` (static images of
          the live editor grids, captured before entry), so a remount is cheap — no Gridd
          iframe ever loads here, so stepping between slides doesn't reload and break the
          immersion. No ±1 preloading. */}
      <div
        className="slide-stage"
        style={{
          width: "min(100vw, calc(100vh * 16 / 9))",
          aspectRatio: "16 / 9",
        }}
      >
        {leaving && (
          <div
            key={`leave-${leaving.slide.id}`}
            className="slide-motion slide-leaving"
            style={motionStyle(transition, leaving.navDir, "leave")}
          >
            <Slide
              key={leaving.slide.id}
              slide={leaving.slide}
              index={leaving.index}
              pageNumber={numbers[leaving.index]}
              onChange={noop}
              presenting
              snapshots={snapshots}
            />
          </div>
        )}
        <div
          key={`cur-${current.id}`}
          className={cn("slide-motion", leaving && "slide-entering")}
          style={leaving ? motionStyle(transition, navDir, "enter") : undefined}
        >
          <Slide
            key={current.id}
            slide={current}
            index={index}
            pageNumber={numbers[index]}
            onChange={noop}
            presenting
            snapshots={snapshots}
          />
        </div>
      </div>
      {below > 0 && (
        <div className="pointer-events-none absolute bottom-4 right-6 select-none rounded-full bg-white/10 px-3 py-1 text-sm tabular-nums text-white/50">
          ↓ {below}
        </div>
      )}
    </div>
  );
}
